import React, { useState, useEffect } from 'react'
import { withTranslation } from 'react-i18next'
import { gql, useLazyQuery } from '@apollo/client'
import Header from '../components/Headers/Header'
import CustomLoader from '../components/Loader/CustomLoader'
import DataTable from 'react-data-table-component'
import { Icon } from '@iconify/react'
import useGlobalStyles from '../utils/globalStyles'
import {
  Container,
  Button,
  Grid,
  Typography,
  Box,
  TextField,
  Autocomplete,
  Paper
} from '@mui/material'
import TableHeader from '../components/TableHeader'
import { customStyles as weeklySalesCustomStyles } from '../utils/tableForWeeklySalesByCategoryData'
import { getallcategories } from '../apollo'

const GET_ALL_CATEGORIES = gql`
  ${getallcategories}
`

const GET_WEEKLY_SALES_BY_CATEGORY = gql`
  query GetWeeklySalesReportByCategory($input: WeeklySalesReportInput!) {
    getWeeklySalesReportByCategory(input: $input) {
      periodLabel
      startDate
      endDate
      items {
        stockName
        orderUnits
        currentStockUnits
        monSales
        tueSales
        wedSales
        thuSales
        friSales
        satSales
        sunSales
      }
    }
  }
`

const WeeklySalesByCategory = (props) => {
  const { t } = props
  const [categories, setCategories] = useState([])
  const [selectedCategory, setSelectedCategory] = useState(null)
  const [reportData, setReportData] = useState({
    periodLabel: '',
    startDate: '',
    endDate: '',
    totalItemsCount: 0,
    items: []
  })
  const [errorMessage, setErrorMessage] = useState('')
  const [hasFetched, setHasFetched] = useState(false)
  const globalClasses = useGlobalStyles()

  const [fetchCategories, { loading: categoriesLoading, error: categoriesError }] = useLazyQuery(
    GET_ALL_CATEGORIES,
    {
      fetchPolicy: 'no-cache',
      variables: { skip: 0, limit: 1000 },
      onCompleted: (data) => {
        const list = (data && data.getCategories && data.getCategories.categories) || []
        // prepend an "All Categories" option with empty id to allow fetching all
        const allOpt = { _id: '', name: 'All Categories' }
        const newList = [allOpt, ...list]
        setCategories(newList)
        if (!selectedCategory && newList.length) {
          setSelectedCategory(newList[0])
        }
      },
      onError: (error) => {
        console.error('Failed to fetch categories:', error)
        setErrorMessage('Failed to load categories.')
      }
    }
  )

  const [fetchWeeklySales, { loading: reportLoading }] = useLazyQuery(
    GET_WEEKLY_SALES_BY_CATEGORY,
    {
      fetchPolicy: 'no-cache',
      onCompleted: (data) => {
        const report = (data && data.getWeeklySalesReportByCategory) || {}
        const items = (report.items && Array.isArray(report.items)) ? report.items : []
        setReportData({
          periodLabel: report.periodLabel || '',
          startDate: report.startDate || '',
          endDate: report.endDate || '',
          totalItemsCount: report.totalItemsCount != null ? report.totalItemsCount : items.length,
          items
        })
        setErrorMessage('')
        setHasFetched(true)
      },
      onError: (error) => {
        console.error('Failed to fetch weekly report:', error)
        const serverMessage = error && error.message
        const friendlyMessage = serverMessage && serverMessage.includes('Cannot return null for non-nullable field WeeklySalesByCategoryReport.totalItemsCount.')
          ? 'No products exist in the selected category.'
          : serverMessage || 'Unable to load weekly report.'
        setErrorMessage(friendlyMessage)
        setHasFetched(true)
      }
    }
  )

  useEffect(() => {
    fetchCategories()
  }, [])

  useEffect(() => {
    if (selectedCategory) {
      const input = selectedCategory._id ? { categoryId: selectedCategory._id } : {}
      fetchWeeklySales({
        variables: {
          input
        }
      })
    }
  }, [selectedCategory])

  const handleCategoryChange = (_, newValue) => {
    setSelectedCategory(newValue)
  }

  const handleDownloadCSV = () => {
    if (!reportData.items || !reportData.items.length) {
      alert('No report data available for download.')
      return
    }

    const csvRows = []
    csvRows.push('Category,"' + ((selectedCategory && selectedCategory.name) || 'N/A') + '"')
    csvRows.push('Period,"' + (reportData.periodLabel || 'Last 7 Days') + '"')
    const formattedItemCount = reportData.totalItemsCount != null ? reportData.totalItemsCount : reportData.items.length
    csvRows.push('Total Items,"' + formattedItemCount + '"')
    csvRows.push('')
    csvRows.push([
      'Stock Name',
      'Order Units',
      'Current Stock Units',
      'Mon Sales',
      'Tue Sales',
      'Wed Sales',
      'Thu Sales',
      'Fri Sales',
      'Sat Sales',
      'Sun Sales'
    ].map((value) => `"${value}"`).join(','))

    reportData.items.forEach((row) => {
      const values = [
        row.stockName || '',
        row.orderUnits != null ? row.orderUnits : 0,
        row.currentStockUnits != null ? row.currentStockUnits : 0,
        row.monSales != null ? row.monSales : 0,
        row.tueSales != null ? row.tueSales : 0,
        row.wedSales != null ? row.wedSales : 0,
        row.thuSales != null ? row.thuSales : 0,
        row.friSales != null ? row.friSales : 0,
        row.satSales != null ? row.satSales : 0,
        row.sunSales != null ? row.sunSales : 0
      ]
      const csvRow = values.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(',')
      csvRows.push(csvRow)
    })

    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const fileName = `Weekly_Sales_By_Category_${((selectedCategory && selectedCategory.name) || 'All_Categories')}.csv`
    link.href = URL.createObjectURL(blob)
    link.setAttribute('download', fileName)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(link.href)
  }

  const displayRange = () => {
    if (reportData.startDate && reportData.endDate) {
      return `${new Date(reportData.startDate).toLocaleDateString()} - ${new Date(reportData.endDate).toLocaleDateString()}`
    }
    const today = new Date()
    const prior = new Date(today)
    prior.setDate(prior.getDate() - 6)
    return `${prior.toLocaleDateString()} - ${today.toLocaleDateString()}`
  }

  const columns = [
    {
      name: '#',
      cell: (_, index) => index + 1,
      width: '70px'
    },
    {
      name: 'Stock Name',
      selector: row => row.stockName || 'N/A',
      sortable: true,
      wrap: true,
      style: {
        textAlign: 'left',
        paddingLeft: '16px'
      }
    },
    {
      name: 'Order Units',
      selector: row => (row.orderUnits != null ? row.orderUnits : ''),
      sortable: true
    },
    {
      name: 'Current Stock Units',
      selector: row => (row.currentStockUnits != null ? row.currentStockUnits : ''),
      sortable: true
    },
    {
      name: 'Mon',
      selector: row => row.monSales || 0,
      sortable: true
    },
    {
      name: 'Tue',
      selector: row => row.tueSales || 0,
      sortable: true
    },
    {
      name: 'Wed',
      selector: row => row.wedSales || 0,
      sortable: true
    },
    {
      name: 'Thu',
      selector: row => row.thuSales || 0,
      sortable: true
    },
    {
      name: 'Fri',
      selector: row => row.friSales || 0,
      sortable: true
    },
    {
      name: 'Sat',
      selector: row => row.satSales || 0,
      sortable: true
    },
    {
      name: 'Sun',
      selector: row => row.sunSales || 0,
      sortable: true
    }
  ]

  return (
    <>
      <Header />
      <Container className={globalClasses.flex}>
        <Box sx={{ mb: 3, mt: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={categories}
                getOptionLabel={(option) => option.name || ''}
                value={selectedCategory}
                onChange={handleCategoryChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('Select Category')}
                    placeholder={t('Search category')}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    sx={{
                      backgroundColor: 'white',
                      '& .MuiInputBase-input': {
                        color: 'black'
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#cfd8dc'
                      }
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} style={{ color: 'black' }}>
                    {option.name}
                  </li>
                )}
                loading={categoriesLoading}
                clearOnEscape
                sx={{ backgroundColor: 'white' }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Button
                onClick={handleDownloadCSV}
                className={globalClasses.dashbordBtn}
                fullWidth
              >
                <Icon icon="ic:round-download" style={{ color: 'white', marginRight: 8 }} />
                {t('Download CSV')}
              </Button>
            </Grid>
          </Grid>
        </Box>

        <Paper sx={{ p: 3, mb: 3 }}>
          <TableHeader title={t('Weekly Sales Report Preview')} />
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#000' }}>
              {t('Category')}: {((selectedCategory && selectedCategory.name) || t('None selected'))}
            </Typography>
            <Typography variant="body2" sx={{ color: '#000', mt: 1 }}>
              {t('Period')}: {displayRange()}
            </Typography>
            <Typography variant="body2" sx={{ color: '#000', mt: 1 }}>
              {t('Total items')}: {(reportData.totalItemsCount != null ? reportData.totalItemsCount : 0)}
            </Typography>
          </Box>

          <Box sx={{ mt: 3 }}>
            {reportLoading ? (
              <CustomLoader />
            ) : errorMessage ? (
              <Typography color="error">{errorMessage}</Typography>
            ) : reportData.items.length ? (
              <DataTable
                columns={columns}
                data={reportData.items}
                noHeader
                pagination
                paginationPerPage={10}
                paginationRowsPerPageOptions={[10, 20, 50]}
                customStyles={weeklySalesCustomStyles}
                highlightOnHover
                pointerOnHover
                responsive
                dense
              />
            ) : (
              <Typography variant="body2" sx={{ color: '#666' }}>
                {hasFetched
                  ? selectedCategory && selectedCategory._id
                    ? t('No products exist in the selected category.')
                    : t('No preview data is available for this category.')
                  : t('Select a category to load a preview.')}
              </Typography>
            )}
          </Box>
        </Paper>
      </Container>
    </>
  )
}

export default withTranslation()(WeeklySalesByCategory)
